import type { ZodIssue } from 'zod';

export type FieldErrors = Record<string, string>;

/**
 * Collapses Zod issues to one message per field.
 *
 * The first issue on a field is the one shown: later issues on the same field
 * are usually consequences of the first, and stacking them under one input
 * reads as noise rather than as help.
 */
export function issuesToFieldErrors(issues: readonly ZodIssue[]): FieldErrors {
  const errors: FieldErrors = {};
  for (const issue of issues) errors[String(issue.path[0] ?? 'root')] ??= issue.message;
  return errors;
}

/**
 * Turns a rejected save into field errors.
 *
 * The server validates with the same schemas as the client, so it can reject
 * something the client accepted — a duplicate hostname, say. When it names
 * fields we show them inline; otherwise the message goes to the form root.
 */
export function submitErrorToFieldErrors(error: unknown, fallback = 'Could not save'): FieldErrors {
  const fields =
    error !== null && typeof error === 'object' && 'fields' in error
      ? (error as { fields: FieldErrors }).fields
      : {};

  if (Object.keys(fields).length > 0) return fields;
  return { root: error instanceof Error ? error.message : fallback };
}
